import { useCallback, useState } from "react";
import { Card } from "../models/Card";
import { StackCard } from "../models/StackCard";

interface CardViewProps {
    card?: Card;
    stackCard?: StackCard;
    isFavorite: boolean;
    isCardSelected: boolean;
    onClicked: (card?: Card) => any;
    onRemoveCard: (card: Card) => any;
    onMarkFavoriteCard: ((card: Card) => any) | null;
    onUnmarkFavoriteCard: ((card: Card) => any) | null;
    onCardCountChanged?: (stackCard: StackCard, newValue: number) => any;
}

export default function CardView(props: CardViewProps) {

    const { isCardSelected, onClicked } = props;
    const card = props.card ? props.card : props.stackCard?.card;

    const [isMouseOver, setIsMouseOver] = useState<boolean | null>(false);
    //const [isOpended, setIsOpened] = useState(false);

    const handleCloseOverlay = useCallback(() => {
        setIsMouseOver(null);
        if(isCardSelected){
            onClicked(undefined);
        }
    }, [onClicked, isCardSelected]);

    const handleOnClick = useCallback(() => {
        if (card) {
            onClicked(card);
        }
    }, [card, onClicked]);

    const handleOnMouseEnter = useCallback(() => {
        if (isMouseOver != null) {
            setIsMouseOver(true);
        }
    }, [isMouseOver]);

    const handleOnMouseLeave = useCallback(() => {
        setIsMouseOver(false);
    }, []);

    if (!card) {
        throw new Error("Required card or stackCard prop.");
    }

    return <>
        <div className='card-element' data-uuid={card.uuid} onClick={handleOnClick} onMouseEnter={handleOnMouseEnter} onMouseLeave={handleOnMouseLeave}>
            <div className="d-flex justify-content-around align-items-center">
                <span className={`card-no ${props.isFavorite ? 'text-primary' : ''}`}>{card.cardNo}</span>
                {
                    props.stackCard ? <StackCountBadge count={props.stackCard.count} /> : null
                }
                {
                    props.isFavorite ? <FavoriteBadge /> : null
                }
            </div>
            <img className='card-image' src={card.url} alt={`${card.cardNo} ${card.cardName}`} />
            <OverlayMenu
                isVisible={isMouseOver || props.isCardSelected}
                stackCard={props.stackCard}
                card={card}
                onRemoveCard={props.onRemoveCard}
                onMarkFavoriteCard={props.onMarkFavoriteCard}
                onUnmarkFavoriteCard={props.onUnmarkFavoriteCard}
                onClose={handleCloseOverlay}
                onCardCountChanged={props.onCardCountChanged} />
            <div className="text-center">
                <small className={props.isFavorite ? 'text-primary' : ''}>{card.cardName}</small>
            </div>
        </div>
    </>;
}

function StackCountBadge(props: { count: number }) {
    return <span className="badge rounded-pill bg-dark">{props.count}枚</span>
}

function FavoriteBadge() {
    return <span className="badge rounded-pill bg-primary">推し</span>
}

interface OverlayMenuProps {
    isVisible: boolean;
    stackCard?: StackCard;
    card: Card;
    onRemoveCard: (card: Card) => any;
    onMarkFavoriteCard: ((card: Card) => any) | null;
    onUnmarkFavoriteCard: ((card: Card) => any) | null;
    onClose: () => any;
    onCardCountChanged?: (stackCard: StackCard, newValue: number) => any;
}

function OverlayMenu(props: OverlayMenuProps) {

    const { isVisible, stackCard, card, onRemoveCard, onMarkFavoriteCard, onUnmarkFavoriteCard, onClose, onCardCountChanged } = props;

    const handleOnClickRemove = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onRemoveCard(card);
    }, [onRemoveCard, card]);

    const handleOnClickFavorite = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        if (onMarkFavoriteCard) {
            event.stopPropagation();
            onMarkFavoriteCard(card);
        }
    }, [onMarkFavoriteCard, card]);

    const handleOnClickClose = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onClose();
    }, [onClose]);

    const handleCardCountChanged = useCallback((newValue: number) => {
        if (stackCard && onCardCountChanged) {
            onCardCountChanged(stackCard, newValue);
        }
    }, [stackCard, onCardCountChanged]);

    const handleOnClickUnFavorite = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        if (onUnmarkFavoriteCard) {
            event.stopPropagation();
            onUnmarkFavoriteCard(card);
        }
    }, [onUnmarkFavoriteCard, card]);

    if (!isVisible) {
        return null;
    }

    //「推し」の場合は「推し解除」ボタンを挿入する
    const isMarkFavoriteDisabled = onMarkFavoriteCard == null || !card.canFavorite;
    const isUnmarkFavoriteDisabled = onUnmarkFavoriteCard == null;

    return <div className="overlay-menu d-grid gap-2 p-1 p-lg-3">
        {
            isUnmarkFavoriteDisabled ?
                <button className={`btn btn-sm btn-primary ${isMarkFavoriteDisabled ? 'disabled' : ''}`} onClick={handleOnClickFavorite} disabled={isMarkFavoriteDisabled}>
                    「推し」に変更
                </button>
                :
                <button className={`btn btn-sm btn-primary`} onClick={handleOnClickUnFavorite}>
                    「推し」をやめる
                </button>
        }

        <NumericUpDown stackCard={props.stackCard} onValueChange={handleCardCountChanged} />
        <button className="btn btn-sm btn-danger" onClick={handleOnClickRemove}>デッキから削除</button>
        <button className="btn btn-sm btn-outline-primary bg-white" onClick={handleOnClickClose}>閉じる</button>
    </div>
}

function NumericUpDown(props: { stackCard?: StackCard, onValueChange: (newValue: number) => any }) {

    const { stackCard, onValueChange } = props;

    const handleOnIncrement = useCallback(() => {
        if (stackCard) {
            onValueChange(stackCard.count + 1);
        }
    }, [stackCard, onValueChange]);

    const handleOnDecrement = useCallback(() => {
        if (stackCard) {
            if (stackCard.count >= 2) {
                onValueChange(stackCard.count - 1);
            }
        }
    }, [stackCard, onValueChange]);

    if (props.stackCard) {
        return <div className="btn-group btn-group-sm bg-white rounded-pill" role="group">
            <button type="button" className="btn btn-primary rounded-pill" onClick={handleOnDecrement}>－</button>
            <button type="button" className="btn counter-center-value">{props.stackCard.count}枚</button>
            <button type="button" className="btn btn-primary rounded-pill" onClick={handleOnIncrement}>＋</button>
        </div>
    } else {
        return <div className="btn-group btn-group-sm rounded-pill" role="group">
            <button type="button" className="btn btn-primary rounded-pill disabled">－</button>
            <button type="button" className="btn counter-center-value">&nbsp;</button>
            <button type="button" className="btn btn-primary rounded-pill disabled">＋</button>
        </div>
    }
}